/**
 * Analytics Schema
 * Usage analytics, sessions, and user tracking tables
 */

import {
  pgTable,
  uuid,
  varchar,
  text,
  timestamp,
  integer,
  numeric,
  boolean,
} from 'drizzle-orm/pg-core';
import { profiles } from './profiles';
import { relations } from 'drizzle-orm';

// =============================================================================
// Usage Analytics
// =============================================================================
export const usageAnalytics = pgTable('usage_analytics', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id'),
  resourceType: varchar('resource_type', { length: 100 }).notNull(),
  resourceId: uuid('resource_id'),
  actionType: varchar('action_type', { length: 100 }).notNull(),
  metadata: text('metadata'), // JSON
  createdAt: timestamp('created_at').defaultNow(),
});

// =============================================================================
// User Sessions
// =============================================================================
export const userSessions = pgTable('user_sessions', {
  id: uuid('id').primaryKey().defaultRandom(),
  sessionId: varchar('session_id', { length: 255 }).notNull(),
  userId: uuid('user_id'),
  startedAt: timestamp('started_at').defaultNow().notNull(),
  endedAt: timestamp('ended_at'),
  sessionDuration: integer('session_duration'),
  pageViews: integer('page_views'),
  bounceRate: numeric('bounce_rate'),
  entryPage: text('entry_page'),
  exitPage: text('exit_page'),
  deviceType: varchar('device_type', { length: 50 }),
  browser: varchar('browser', { length: 100 }),
  operatingSystem: varchar('operating_system', { length: 100 }),
  screenResolution: varchar('screen_resolution', { length: 50 }),
  userAgent: text('user_agent'),
  conversionEvents: text('conversion_events'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// User Interactions
// =============================================================================
export const userInteractions = pgTable('user_interactions', {
  id: uuid('id').primaryKey().defaultRandom(),
  sessionId: varchar('session_id', { length: 255 }).notNull(),
  userId: uuid('user_id'),
  interactionType: varchar('interaction_type', { length: 50 }).notNull(),
  pageUrl: text('page_url').notNull(),
  elementSelector: text('element_selector'),
  elementText: text('element_text'),
  scrollDepth: integer('scroll_depth'),
  viewportWidth: integer('viewport_width'),
  viewportHeight: integer('viewport_height'),
  interactionData: text('interaction_data'), // JSON
  timestamp: timestamp('timestamp').defaultNow().notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// User Flows
// =============================================================================
export const userFlows = pgTable('user_flows', {
  id: uuid('id').primaryKey().defaultRandom(),
  sessionId: varchar('session_id', { length: 255 }).notNull(),
  userId: uuid('user_id'),
  flowName: varchar('flow_name', { length: 255 }),
  flowData: text('flow_data').notNull(), // JSON
  startedAt: timestamp('started_at').defaultNow().notNull(),
  completedAt: timestamp('completed_at'),
  completionStatus: varchar('completion_status', { length: 50 }),
  completionTime: integer('completion_time'),
  dropOffPoint: text('drop_off_point'),
  conversionValue: numeric('conversion_value'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Error Logs
// =============================================================================
export const errorLogs = pgTable('error_logs', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id'),
  url: text('url').notNull(),
  message: text('message').notNull(),
  stack: text('stack'),
  context: text('context'), // JSON
  timestamp: timestamp('timestamp').defaultNow().notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Performance Alerts
// =============================================================================
export const performanceAlerts = pgTable('performance_alerts', {
  id: uuid('id').primaryKey().defaultRandom(),
  alertType: varchar('alert_type', { length: 100 }).notNull(),
  severity: varchar('severity', { length: 50 }).notNull(),
  title: varchar('title', { length: 255 }).notNull(),
  description: text('description'),
  affectedUrl: text('affected_url'),
  metricData: text('metric_data'), // JSON
  thresholdExceeded: text('threshold_exceeded'), // JSON
  isResolved: boolean('is_resolved').default(false),
  resolvedAt: timestamp('resolved_at'),
  resolvedBy: uuid('resolved_by'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Knowledge Items (Agent system)
// =============================================================================
export const knowledgeItems = pgTable('knowledge_items', {
  id: varchar('id', { length: 255 }).primaryKey(),
  itemType: varchar('item_type', { length: 100 }).notNull(),
  data: text('data').notNull(), // JSON
  source: varchar('source', { length: 255 }),
  category: varchar('category', { length: 100 }),
  tags: text('tags'), // JSON array
  version: varchar('version', { length: 50 }),
  expires: timestamp('expires'),
  timestamp: timestamp('timestamp').defaultNow().notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Relations
// =============================================================================
export const usageAnalyticsRelations = relations(usageAnalytics, ({ one }) => ({
  user: one(profiles, {
    fields: [usageAnalytics.userId],
    references: [profiles.id],
  }),
}));

// =============================================================================
// Types
// =============================================================================
export type UsageAnalytic = typeof usageAnalytics.$inferSelect;
export type NewUsageAnalytic = typeof usageAnalytics.$inferInsert;
export type UserSession = typeof userSessions.$inferSelect;
export type NewUserSession = typeof userSessions.$inferInsert;
export type UserInteraction = typeof userInteractions.$inferSelect;
export type NewUserInteraction = typeof userInteractions.$inferInsert;
export type UserFlow = typeof userFlows.$inferSelect;
export type NewUserFlow = typeof userFlows.$inferInsert;
export type ErrorLog = typeof errorLogs.$inferSelect;
export type NewErrorLog = typeof errorLogs.$inferInsert;
export type PerformanceAlert = typeof performanceAlerts.$inferSelect;
export type NewPerformanceAlert = typeof performanceAlerts.$inferInsert;
export type KnowledgeItem = typeof knowledgeItems.$inferSelect;
export type NewKnowledgeItem = typeof knowledgeItems.$inferInsert;
